// ============================================
// Nebula and Galaxy Shaders
// Fragment shaders for deep space objects
// ============================================

import { noiseFunction } from './common';

/**
 * Giant nebula fragment shader - detailed and volumetric
 */
export const giantNebulaFragmentShader = `
    uniform float time;
    uniform vec3 color1;
    uniform vec3 color2;
    uniform vec3 color3;
    uniform float alpha;
    uniform float seed;
    varying vec2 vUv;

    ${noiseFunction}

    void main() {
        vec2 uv = vUv - 0.5;
        float t = time * 0.012;

        // Multi-layer complex noise
        float n1 = fbm(uv * 1.5 + seed + t * 0.3);
        float n2 = fbm(uv * 2.8 + seed * 1.7 - t * 0.2);
        float n3 = fbm(uv * 4.5 + vec2(n1, n2) * 0.4 + seed * 2.3);
        float n4 = fbm(uv * 7.0 + seed * 3.1);
        float n5 = fbm(uv * 12.0 + seed * 4.2 + t * 0.1);

        // Main gas cloud - more volumetric
        float gas = n1 * 0.35 + n2 * 0.3 + n3 * 0.2 + n4 * 0.15;
        gas = smoothstep(0.05, 0.55, gas + 0.35);

        // Dynamic filaments
        float filament1 = abs(sin(n1 * 10.0 + n2 * 6.0 + t));
        filament1 = pow(filament1, 4.0) * 0.6;
        float filament2 = abs(cos(n2 * 8.0 + n3 * 4.0 - t * 0.5));
        filament2 = pow(filament2, 5.0) * 0.4;

        // Spiral structure
        float angle = atan(uv.y, uv.x);
        float spiral = sin(angle * 3.0 + length(uv) * 8.0 + n1 * 4.0 + t);
        spiral = pow(abs(spiral), 2.0) * 0.3;

        // Volumetric edge - fills screen nicely
        float dist = length(uv * vec2(0.9, 1.4));
        float edge = 1.0 - smoothstep(0.35, 0.55, dist);
        edge = pow(edge, 0.8);

        // Rich color calculation
        vec3 col = mix(color1, color2, n1 * 0.7 + 0.3);
        col = mix(col, color3, n2 * 0.5 + n3 * 0.2);
        col += color2 * filament1 * 0.5;
        col += color1 * filament2 * 0.4;
        col += mix(color1, color3, 0.5) * spiral;

        // Glowing star formation regions
        float bright = smoothstep(0.5, 0.72, n3) * gas;
        col += vec3(0.5, 0.45, 0.6) * bright * 1.2;

        // Bright core
        float core = 1.0 - smoothstep(0.0, 0.2, dist);
        core = pow(core, 2.5);
        col += mix(color2, vec3(1.0, 0.95, 0.9), 0.5) * core * 0.6;

        // Dust lanes
        float dust = smoothstep(0.55, 0.75, n4) * 0.25;
        col *= (1.0 - dust * 0.5);

        // Star sparkles (multiple layers)
        float stars1 = snoise(uv * 80.0 + seed);
        stars1 = smoothstep(0.82, 0.95, stars1) * gas;
        float stars2 = snoise(uv * 150.0 + seed * 2.0);
        stars2 = smoothstep(0.88, 0.98, stars2) * gas * 0.5;
        col += vec3(1.0, 0.98, 0.95) * stars1 * 0.55;
        col += vec3(0.95, 0.98, 1.0) * stars2 * 0.4;

        // Fine detail
        float detail = n5 * 0.12;
        col += col * detail;

        float finalAlpha = (gas + filament1 * 0.25 + spiral * 0.15) * edge * alpha * 0.7;
        gl_FragColor = vec4(col, finalAlpha);
    }
`;

/**
 * Globular cluster shader - M13 style
 */
export const globularClusterFragmentShader = `
    uniform float time;
    uniform vec3 coreColor;
    uniform vec3 outerColor;
    uniform float alpha;
    uniform float seed;
    varying vec2 vUv;

    ${noiseFunction}

    void main() {
        vec2 uv = vUv - 0.5;
        float r = length(uv);

        // Dense core
        float core = 1.0 - smoothstep(0.0, 0.15, r);
        core = pow(core, 1.5);

        // Stars spreading outward
        float outer = 1.0 - smoothstep(0.0, 0.4, r);
        outer = pow(outer, 2.0);

        // Multi-layer stars at different scales
        float stars1 = snoise(uv * 100.0 + seed);
        float stars2 = snoise(uv * 70.0 + seed * 1.5);
        float stars3 = snoise(uv * 45.0 + seed * 2.0);
        float stars4 = snoise(uv * 25.0 + seed * 3.0);

        stars1 = smoothstep(0.4, 0.75, stars1) * core;
        stars2 = smoothstep(0.5, 0.8, stars2) * (core * 0.8 + outer * 0.2);
        stars3 = smoothstep(0.6, 0.85, stars3) * outer;
        stars4 = smoothstep(0.75, 0.92, stars4) * outer * 0.5;

        float allStars = stars1 + stars2 * 0.8 + stars3 * 0.5 + stars4 * 0.3;

        // Core glow
        float glow = core * 0.4 + outer * 0.15;

        vec3 col = mix(coreColor, outerColor, smoothstep(0.0, 0.35, r));
        col = col * allStars + vec3(0.95, 0.9, 0.8) * glow;

        float finalAlpha = (allStars + glow) * alpha;
        gl_FragColor = vec4(col, finalAlpha);
    }
`;

/**
 * Spiral galaxy fragment shader
 */
export const spiralGalaxyFragmentShader = `
    uniform float time;
    uniform vec3 coreColor;
    uniform vec3 armColor;
    uniform vec3 dustColor;
    uniform float alpha;
    uniform float seed;
    uniform float tilt;
    uniform float arms;
    varying vec2 vUv;

    ${noiseFunction}

    void main() {
        vec2 uv = vUv - 0.5;

        // Tilt to create elliptical shape
        float tiltAmount = tilt;
        uv.y *= tiltAmount;

        float r = length(uv);
        float angle = atan(uv.y, uv.x);
        float rotation = time * 0.006;
        angle += rotation;

        // Complex spiral arms
        float armCount = arms;
        float spiralTightness = 4.0;

        // Main spiral arms
        float spiral1 = sin(angle * armCount - log(r + 0.08) * spiralTightness + seed);
        spiral1 = spiral1 * 0.5 + 0.5;
        spiral1 = pow(spiral1, 2.5);

        // Secondary spiral arms (thinner)
        float spiral2 = sin(angle * armCount - log(r + 0.08) * spiralTightness + seed + 1.57);
        spiral2 = spiral2 * 0.5 + 0.5;
        spiral2 = pow(spiral2, 3.5) * 0.5;

        float spiral = spiral1 + spiral2;

        // Arm width spreads from core outward
        float armWidth = smoothstep(0.0, 0.25, r) * (1.0 - smoothstep(0.3, 0.48, r));
        spiral *= armWidth;

        // Add irregularity with noise
        float n1 = fbm(uv * 6.0 + seed);
        float n2 = fbm(uv * 12.0 + seed * 1.5);
        spiral *= (0.6 + n1 * 0.5);

        // Bright elliptical bulge
        float bulge = 1.0 - smoothstep(0.0, 0.12, r);
        bulge = pow(bulge, 2.0);

        // Bulge glow for 3D effect
        float bulgeGlow = 1.0 - smoothstep(0.0, 0.2, r);
        bulgeGlow = pow(bulgeGlow, 1.5) * 0.4;

        // Outer halo
        float halo = 1.0 - smoothstep(0.0, 0.5, r);
        halo = pow(halo, 3.5) * 0.2;

        // Dust lanes (characteristic of tilted galaxies)
        float dustLane = smoothstep(-0.02, 0.0, uv.y / tiltAmount) * smoothstep(0.02, 0.0, uv.y / tiltAmount);
        dustLane *= smoothstep(0.05, 0.15, r) * (1.0 - smoothstep(0.28, 0.4, r));
        dustLane *= (0.5 + n2 * 0.5);

        // Color calculation
        vec3 col = vec3(0.0);

        // Bulge (bright yellow-white)
        col += coreColor * bulge * 1.4;
        col += coreColor * bulgeGlow * 0.6;

        // Spiral arms (bluish color)
        col += armColor * spiral * 0.7;

        // Halo
        col += dustColor * halo;

        // Darken with dust lanes (3D effect)
        col *= (1.0 - dustLane * 0.6);

        // Star formation regions in arms (blue dots)
        float starFormation = smoothstep(0.5, 0.7, n2) * spiral;
        col += vec3(0.5, 0.6, 0.9) * starFormation * 0.3;

        // Star sparkles
        float stars1 = snoise(uv * 60.0 + seed);
        stars1 = smoothstep(0.75, 0.92, stars1);
        float stars2 = snoise(uv * 100.0 + seed * 2.0);
        stars2 = smoothstep(0.85, 0.97, stars2);
        col += vec3(1.0, 0.98, 0.95) * stars1 * (spiral * 0.5 + bulge * 0.3) * 0.4;
        col += vec3(0.95, 0.97, 1.0) * stars2 * spiral * 0.25;

        float edge = 1.0 - smoothstep(0.38, 0.5, r);
        float finalAlpha = (bulge * 1.2 + bulgeGlow + spiral * 0.7 + halo) * edge * alpha * 0.8;
        gl_FragColor = vec4(col, finalAlpha);
    }
`;

/**
 * Andromeda-style galaxy shader (tilted large spiral galaxy)
 */
export const andromedaGalaxyFragmentShader = `
    uniform float time;
    uniform vec3 coreColor;
    uniform vec3 armColor;
    uniform vec3 dustColor;
    uniform float alpha;
    uniform float seed;
    varying vec2 vUv;

    ${noiseFunction}

    void main() {
        vec2 uv = vUv - 0.5;

        // Tilt (nearly edge-on galaxy)
        float tiltAmount = 3.5;
        vec2 tiltedUv = uv;
        tiltedUv.y *= tiltAmount;

        float r = length(tiltedUv);
        float angle = atan(tiltedUv.y, tiltedUv.x);
        float rotation = time * 0.004;
        angle += rotation;

        // Multiple spiral arms
        float spiral1 = sin(angle * 2.0 - log(r + 0.06) * 4.5 + seed);
        spiral1 = spiral1 * 0.5 + 0.5;
        spiral1 = pow(spiral1, 2.0);

        float spiral2 = sin(angle * 2.0 - log(r + 0.06) * 4.5 + seed + 3.14);
        spiral2 = spiral2 * 0.5 + 0.5;
        spiral2 = pow(spiral2, 2.5) * 0.6;

        float spiral = spiral1 + spiral2;

        // Arm width
        float armWidth = smoothstep(0.0, 0.2, r) * (1.0 - smoothstep(0.32, 0.48, r));
        spiral *= armWidth;

        // Add irregularity with noise
        float n1 = fbm(tiltedUv * 8.0 + seed);
        float n2 = fbm(tiltedUv * 15.0 + seed * 1.5);
        spiral *= (0.5 + n1 * 0.6);

        // Bright elliptical bulge
        float bulge = 1.0 - smoothstep(0.0, 0.1, r);
        bulge = pow(bulge, 1.8);

        // Bulge glow
        float bulgeGlow = 1.0 - smoothstep(0.0, 0.18, r);
        bulgeGlow = pow(bulgeGlow, 1.5) * 0.35;

        // Wide halo
        float halo = 1.0 - smoothstep(0.0, 0.5, r);
        halo = pow(halo, 3.0) * 0.25;

        // Dust lane (dark band along galactic plane) - key for 3D effect
        float originalY = uv.y;
        float dustWidth = 0.015 + n2 * 0.008;
        float dustLane = smoothstep(dustWidth, 0.0, abs(originalY)) * 0.5;
        dustLane *= smoothstep(0.04, 0.12, r) * (1.0 - smoothstep(0.28, 0.42, r));

        // Color calculation
        vec3 col = vec3(0.0);

        // Bulge
        col += coreColor * bulge * 1.3;
        col += coreColor * bulgeGlow * 0.5;

        // Spiral arms
        col += armColor * spiral * 0.6;

        // Halo
        col += dustColor * halo;

        // Darken with dust lanes
        col *= (1.0 - dustLane * 0.7);

        // Star formation regions in arms
        float starFormation = smoothstep(0.55, 0.75, n2) * spiral;
        col += vec3(0.45, 0.55, 0.85) * starFormation * 0.25;

        // Star sparkles
        float stars1 = snoise(tiltedUv * 70.0 + seed);
        stars1 = smoothstep(0.75, 0.93, stars1);
        float stars2 = snoise(tiltedUv * 120.0 + seed * 2.0);
        stars2 = smoothstep(0.88, 0.98, stars2);
        col += vec3(1.0, 0.98, 0.95) * stars1 * (spiral * 0.4 + bulge * 0.25 + halo * 0.2) * 0.45;
        col += vec3(0.95, 0.97, 1.0) * stars2 * spiral * 0.2;

        float edge = 1.0 - smoothstep(0.4, 0.52, r);
        float finalAlpha = (bulge * 1.2 + bulgeGlow + spiral * 0.6 + halo) * edge * alpha * 0.85;
        gl_FragColor = vec4(col, finalAlpha);
    }
`;
